import { createPluginRegistration, type PluginBatchRegistrations } from "@embedpdf/core";
import { AnnotationPluginPackage, LockModeType } from "@embedpdf/plugin-annotation/react";
import { DocumentManagerPluginPackage } from "@embedpdf/plugin-document-manager/react";
import { ExportPluginPackage } from "@embedpdf/plugin-export/react";
import { HistoryPluginPackage } from "@embedpdf/plugin-history/react";
import { InteractionManagerPluginPackage } from "@embedpdf/plugin-interaction-manager/react";
import { RenderPluginPackage } from "@embedpdf/plugin-render/react";
import { ScrollPluginPackage } from "@embedpdf/plugin-scroll/react";
import { SelectionPluginPackage } from "@embedpdf/plugin-selection/react";
import { SignatureMode, SignaturePluginPackage } from "@embedpdf/plugin-signature/react";
import { ViewportPluginPackage } from "@embedpdf/plugin-viewport/react";
import { ZoomMode, ZoomPluginPackage } from "@embedpdf/plugin-zoom/react";
import { SIGNATURE_SIZE } from "./signature";
import { COLOURS, LINK_CATEGORY, TOOL_OVERRIDES } from "./tools";

// A fresh set per document, which is what makes each one its own undo history and its own set of placed marks.
export function pluginsFor(name: string, bytes: Uint8Array): PluginBatchRegistrations {
  return [
    createPluginRegistration(DocumentManagerPluginPackage, {
      // Copied into the engine's worker rather than transferred, so a password asked for later retries these bytes.
      initialDocuments: [{ buffer: bytes.slice().buffer, name }],
      maxDocuments: 1,
    }),
    createPluginRegistration(ViewportPluginPackage, { viewportGap: 16 }),
    createPluginRegistration(ScrollPluginPackage),
    createPluginRegistration(RenderPluginPackage),
    createPluginRegistration(ZoomPluginPackage, { defaultZoomLevel: ZoomMode.FitWidth }),
    createPluginRegistration(InteractionManagerPluginPackage),
    createPluginRegistration(SelectionPluginPackage),
    createPluginRegistration(HistoryPluginPackage),
    createPluginRegistration(AnnotationPluginPackage, {
      // Written into every mark as its author, where the engine's own default is `Guest`; this page knows no name.
      annotationAuthor: "",
      colorPresets: COLOURS.map(({ value }) => value),
      tools: TOOL_OVERRIDES,
      // A link in the document is followed when clicked rather than selected and dragged like a mark made here.
      locked: { type: LockModeType.Include, categories: [LINK_CATEGORY] },
      // Its own opener would open any address, `javascript:` included; `links.ts` opens the ones worth following.
      autoOpenLinks: false,
    }),
    createPluginRegistration(SignaturePluginPackage, {
      mode: SignatureMode.SignatureOnly,
      defaultSize: SIGNATURE_SIZE,
    }),
    createPluginRegistration(ExportPluginPackage),
  ];
}
